import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { 
  Users, Link2, Copy, Check, Share2, Gift, 
  TrendingUp, Award, Crown, ChevronRight, 
  RefreshCw, ExternalLink, Sparkles, Trophy,
  UserPlus, Zap, Clock, ArrowRight, Twitter,
  MessageCircle, Send
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useApp } from "../contexts/AppContext";
import { API, BURNZ_LOGO } from "../constants";

// Format numbers
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
};

// Tier badges based on referral count
const getReferralTier = (count) => {
  if (count >= 100) return { name: "Legend", color: "from-yellow-400 to-orange-500", icon: Crown };
  if (count >= 50) return { name: "Champion", color: "from-purple-400 to-pink-500", icon: Trophy };
  if (count >= 25) return { name: "Ambassador", color: "from-blue-400 to-cyan-500", icon: Award };
  if (count >= 10) return { name: "Advocate", color: "from-green-400 to-emerald-500", icon: Users };
  if (count >= 5) return { name: "Supporter", color: "from-orange-400 to-red-500", icon: UserPlus };
  return { name: "Starter", color: "from-gray-400 to-gray-500", icon: Sparkles };
};

// Share options
const SHARE_OPTIONS = [
  { id: 'twitter', name: 'Twitter', icon: Twitter, color: 'bg-[#1DA1F2]' },
  { id: 'telegram', name: 'Telegram', icon: Send, color: 'bg-[#0088cc]' },
  { id: 'discord', name: 'Discord', icon: MessageCircle, color: 'bg-[#5865F2]' },
];

const ReferralDashboard = () => {
  const { user, walletConnected } = useApp();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  const [referralData, setReferralData] = useState({
    referral_code: null,
    referral_link: null,
    total_referrals: 0,
    referrals: [],
    xp_earned_from_referrals: 0
  });
  
  const [leaderboard, setLeaderboard] = useState([]);
  const [systemStats, setSystemStats] = useState(null);

  // Fetch public data (leaderboard & stats)
  const fetchPublicData = useCallback(async () => {
    try {
      // Fetch leaderboard
      const leaderboardResponse = await fetch(`${API}/referrals/leaderboard?limit=10`);
      if (leaderboardResponse.ok) {
        const data = await leaderboardResponse.json();
        setLeaderboard(data.leaderboard || []);
      }
      
      // Fetch system stats
      const statsResponse = await fetch(`${API}/referrals/stats`);
      if (statsResponse.ok) {
        const data = await statsResponse.json();
        setSystemStats(data);
      }
    } catch (err) {
    }
  }, []);

  // Fetch user's referral data
  const fetchReferralData = useCallback(async () => {
    if (!walletConnected) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('burnz_token');
      
      // Fetch user's referral info
      const myResponse = await fetch(`${API}/referrals/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (myResponse.ok) {
        const data = await myResponse.json();
        setReferralData(data);
      }
      
    } catch (err) {
    }
    setLoading(false);
  }, [walletConnected]);

  useEffect(() => {
    fetchPublicData();
  }, [fetchPublicData]);

  useEffect(() => {
    fetchReferralData();
  }, [fetchReferralData]);

  // Generate referral code
  const generateReferralCode = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/referrals/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code_type: 'user' })
      });
      
      if (response.ok) {
        const data = await response.json();
        setReferralData(prev => ({
          ...prev,
          referral_code: data.referral_code,
          referral_link: data.referral_link
        }));
      }
    } catch (err) {
    }
    setGenerating(false);
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Share handlers
  const handleShare = (platform) => {
    const text = `🔥 Join Burnz Union and earn rewards! Use my referral code: ${referralData.referral_code}`;
    const url = referralData.referral_link || `https://burnzunion.com/join?ref=${referralData.referral_code}`;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      discord: `https://discord.com/channels/@me?message=${encodeURIComponent(text + ' ' + url)}`
    };
    
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  const tier = getReferralTier(referralData.total_referrals);
  const TierIcon = tier.icon;
  const nextTierThreshold = referralData.total_referrals < 5 ? 5 : 
                           referralData.total_referrals < 10 ? 10 :
                           referralData.total_referrals < 25 ? 25 :
                           referralData.total_referrals < 50 ? 50 : 100;
  const progressToNextTier = (referralData.total_referrals / nextTierThreshold) * 100;

  // Show preview mode indicator if not connected
  const isPreviewMode = !walletConnected;

  return (
    <div className="min-h-screen bg-[#0D0D0D] p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400 text-sm">Preview Mode - Connect wallet to generate your referral code</span>
            </div>
            <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-black">
              Connect Wallet
            </Button>
          </motion.div>
        )}

        {/* Header */}
        <div className="mb-8">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-white flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            Referral Dashboard
          </motion.h1>
          <p className="text-white/50 mt-2">Invite friends, earn XP rewards</p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Referral Card & QR */}
          <div className="lg:col-span-2 space-y-6">
            {/* Referral Code Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-gradient-to-br from-purple-900/30 to-pink-900/20 border-purple-500/20 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* QR Code Section */}
                    <div className="flex-shrink-0">
                      <div className="bg-white p-4 rounded-2xl shadow-xl">
                        {referralData.referral_code ? (
                          <QRCodeSVG 
                            value={referralData.referral_link || `https://burnzunion.com/join?ref=${referralData.referral_code}`}
                            size={160}
                            level="H"
                            includeMargin={false}
                            imageSettings={{
                              src: BURNZ_LOGO,
                              height: 30,
                              width: 30,
                              excavate: true
                            }}
                          />
                        ) : (
                          <div className="w-40 h-40 flex items-center justify-center bg-gray-100 rounded-lg">
                            <span className="text-gray-400 text-sm">Generate code first</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Code & Link Section */}
                    <div className="flex-1 space-y-4">
                      <div>
                        <label className="text-white/50 text-sm mb-2 block">Your Referral Code</label>
                        {referralData.referral_code ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-white/10 rounded-xl px-4 py-3 font-mono text-xl font-bold text-white">
                              {referralData.referral_code}
                            </div>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => copyToClipboard(referralData.referral_code)}
                                    className="text-white/70 hover:text-white hover:bg-white/10"
                                  >
                                    {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Copy code</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        ) : (
                          <Button
                            onClick={generateReferralCode}
                            disabled={generating}
                            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white py-6"
                          >
                            {generating ? (
                              <>
                                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-5 h-5 mr-2" />
                                Generate Referral Code
                              </>
                            )}
                          </Button>
                        )}
                      </div>

                      {referralData.referral_code && (
                        <>
                          <div>
                            <label className="text-white/50 text-sm mb-2 block">Referral Link</label>
                            <div className="flex items-center gap-2">
                              <Input
                                readOnly
                                value={referralData.referral_link || `https://burnzunion.com/join?ref=${referralData.referral_code}`}
                                className="bg-white/5 border-white/10 text-white/70 text-sm"
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(referralData.referral_link || `https://burnzunion.com/join?ref=${referralData.referral_code}`)}
                                className="text-white/70 hover:text-white hover:bg-white/10"
                              >
                                <Link2 className="w-5 h-5" />
                              </Button>
                            </div>
                          </div>

                          {/* Share Buttons */}
                          <div>
                            <label className="text-white/50 text-sm mb-2 block">Share</label>
                            <div className="flex gap-2">
                              {SHARE_OPTIONS.map(option => (
                                <Button
                                  key={option.id}
                                  variant="ghost"
                                  onClick={() => handleShare(option.id)}
                                  className={`${option.color} text-white hover:opacity-90 flex-1`}
                                >
                                  <option.icon className="w-4 h-4 mr-2" />
                                  {option.name}
                                </Button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="bg-surface-2 border-white/5">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-purple-400" />
                      <span className="text-xs text-white/50">Total Referrals</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{referralData.total_referrals}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                <Card className="bg-surface-2 border-white/5">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-orange-400" />
                      <span className="text-xs text-white/50">XP Earned</span>
                    </div>
                    <p className="text-2xl font-bold text-orange-400">{formatNumber(referralData.xp_earned_from_referrals)}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="bg-surface-2 border-white/5">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Gift className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-white/50">Per Referral</span>
                    </div>
                    <p className="text-2xl font-bold text-green-400">100 XP</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <Card className="bg-surface-2 border-white/5">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-blue-400" />
                      <span className="text-xs text-white/50">Bonus XP</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-400">50 XP</p>
                    <p className="text-[10px] text-white/30">for new users</p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Recent Referrals */}
            <Card className="bg-surface-2 border-white/5">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-400" />
                  Recent Referrals
                </CardTitle>
              </CardHeader>
              <CardContent>
                {referralData.referrals && referralData.referrals.length > 0 ? (
                  <div className="space-y-3">
                    {referralData.referrals.map((ref, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                            {ref.wallet?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="text-white font-medium">{ref.wallet}</p>
                            <p className="text-white/30 text-xs">
                              {new Date(ref.joined_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400 border-0">
                          +100 XP
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto mb-3 text-white/20" />
                    <p className="text-white/50">No referrals yet</p>
                    <p className="text-white/30 text-sm">Share your code to start earning!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Tier & Leaderboard */}
          <div className="space-y-6">
            {/* Tier Progress Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Card className={`bg-gradient-to-br ${tier.color} border-0 overflow-hidden`}>
                <CardContent className="p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                      <TierIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Current Tier</p>
                      <p className="text-xl font-bold">{tier.name}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/70">Progress to next tier</span>
                      <span className="font-medium">{referralData.total_referrals}/{nextTierThreshold}</span>
                    </div>
                    <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-white rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(progressToNextTier, 100)}%` }}
                        transition={{ duration: 1 }}
                      />
                    </div>
                    <p className="text-white/50 text-xs">
                      {nextTierThreshold - referralData.total_referrals} more referrals to unlock next tier
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Tier Benefits */}
            <Card className="bg-surface-2 border-white/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Award className="w-4 h-4 text-yellow-400" />
                  Tier Benefits
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { tier: "Starter", req: "0+", benefit: "Basic referral rewards" },
                  { tier: "Supporter", req: "5+", benefit: "+10% bonus XP" },
                  { tier: "Advocate", req: "10+", benefit: "+25% bonus XP" },
                  { tier: "Ambassador", req: "25+", benefit: "+50% bonus XP + Badge" },
                  { tier: "Champion", req: "50+", benefit: "+75% bonus XP + Exclusive NFT" },
                  { tier: "Legend", req: "100+", benefit: "+100% bonus XP + VIP Access" },
                ].map((t, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center justify-between p-2 rounded-lg ${t.tier === tier.name ? 'bg-primary-theme/20 border border-primary-theme/30' : 'bg-white/5'}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-white/70 text-xs w-8">{t.req}</span>
                      <span className={`text-sm ${t.tier === tier.name ? 'text-primary-theme font-medium' : 'text-white/70'}`}>
                        {t.tier}
                      </span>
                    </div>
                    <span className="text-white/50 text-xs">{t.benefit}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Leaderboard */}
            <Card className="bg-surface-2 border-white/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-yellow-400" />
                  Top Referrers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leaderboard.length > 0 ? (
                  <div className="space-y-2">
                    {leaderboard.slice(0, 5).map((entry, i) => (
                      <div 
                        key={i} 
                        className={`flex items-center gap-3 p-2 rounded-lg ${
                          i === 0 ? 'bg-yellow-500/10' : 
                          i === 1 ? 'bg-gray-400/10' : 
                          i === 2 ? 'bg-amber-600/10' : 'bg-white/5'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          i === 0 ? 'bg-yellow-500 text-black' : 
                          i === 1 ? 'bg-gray-300 text-black' : 
                          i === 2 ? 'bg-amber-600 text-white' : 'bg-white/10 text-white'
                        }`}>
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">
                            {entry.wallet_preview || 'Anonymous'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-purple-400 text-sm font-bold">{entry.referral_count}</p>
                          <p className="text-white/30 text-[10px]">referrals</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-white/50 text-sm">No referrers yet</p>
                    <p className="text-white/30 text-xs">Be the first!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* System Stats */}
            {systemStats && (
              <Card className="bg-white/5 border-white/5">
                <CardContent className="p-4">
                  <p className="text-white/50 text-xs mb-3">Community Stats</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-white font-bold">{systemStats.users_with_referral_codes}</p>
                      <p className="text-white/30 text-xs">Active referrers</p>
                    </div>
                    <div>
                      <p className="text-white font-bold">{systemStats.total_successful_referrals}</p>
                      <p className="text-white/30 text-xs">Total referrals</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { ReferralDashboard };
export default ReferralDashboard;
